// src/AdminUserManagement.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './styles/ManagementPages.css';

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const AdminUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [editUserId, setEditUserId] = useState(null);
  const [editUsername, setEditUsername] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const authToken = localStorage.getItem('token');

  const fetchUsers = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get('/api/members', {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setUsers(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la récupération des utilisateurs');
    }
  }, [authToken]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = async (userId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) return;
    setError('');
    setFeedback('');
    try {
      await axios.delete(`/api/members/${userId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setFeedback('Utilisateur supprimé.');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de la suppression de l'utilisateur");
    }
  };

  const handleEdit = (user) => {
    setEditUserId(user.id);
    setEditUsername(user.username);
    setEditEmail(user.email);
    setEditPassword('');
    setError('');
    setFeedback('');
  };

  const handleCancelEdit = () => {
    setEditUserId(null);
    setEditUsername('');
    setEditEmail('');
    setEditPassword('');
  };

  const handleSaveEdit = async (userId) => {
    setError('');
    setFeedback('');
    try {
      const payload = { username: editUsername, email: editEmail };
      if (editPassword.trim() !== '') {
        payload.password = editPassword;
      }
      await axios.put(`/api/members/${userId}`, payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setFeedback('Profil mis à jour.');
      fetchUsers();
      handleCancelEdit();
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de la mise à jour de l'utilisateur");
    }
  };

  const stats = useMemo(() => {
    if (!Array.isArray(users) || users.length === 0) {
      return {
        total: 0,
        admins: 0,
        lastSignup: 'Aucune inscription',
        domains: [],
      };
    }

    const admins = users.filter((user) => user.role === 'admin').length;
    let lastSignupDate = null;
    const domainSet = new Map();

    users.forEach((user) => {
      if (user.email) {
        const domain = user.email.split('@')[1];
        if (domain) {
          domainSet.set(domain, (domainSet.get(domain) || 0) + 1);
        }
      }

      if (user.created_at) {
        const date = new Date(user.created_at);
        if (!Number.isNaN(date.getTime()) && (!lastSignupDate || date > lastSignupDate)) {
          lastSignupDate = date;
        }
      }
    });

    const topDomains = Array.from(domainSet.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([domain, count]) => `${domain} (${count})`);

    return {
      total: users.length,
      admins,
      lastSignup: lastSignupDate
        ? lastSignupDate.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
          })
        : 'Aucune inscription',
      domains: topDomains,
    };
  }, [users]);

  return (
    <div className="management-page">
      <div className="management-page__inner">
        <header className="management-header glass-panel">
          <div className="management-header__content">
            <h1 className="management-title">
              <span className="management-title__icon">🧑‍💼</span>
              Administration des membres
            </h1>
            <p className="management-subtitle">
              Supervisez les comptes collaborateurs, ajustez leurs informations et gardez un
              historique clair de chaque inscription.
            </p>
            <div className="management-meta">
              <span className="management-badge">Vision consolidée</span>
              <span className="management-badge">Contrôle en temps réel</span>
            </div>
          </div>
          <div className="management-header__actions">
            <Link to="/create-user" className="mgmt-btn mgmt-btn--primary">
              Créer un utilisateur
            </Link>
          </div>
        </header>

        <section className="management-stats">
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Utilisateurs actifs</span>
            <span className="management-stat-value">{stats.total}</span>
            <span className="management-stat-meta">Comptes au catalogue</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Administrateurs</span>
            <span className="management-stat-value">{stats.admins}</span>
            <span className="management-stat-meta">Rôle admin détecté</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Dernière inscription</span>
            <span className="management-stat-value">{stats.lastSignup}</span>
            <span className="management-stat-meta">Synchronisation membership</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Domaines principaux</span>
            <span className="management-stat-value">
              {stats.domains.length > 0 ? stats.domains[0] : 'n/a'}
            </span>
            <span className="management-stat-meta">
              {stats.domains.slice(1).join(' · ') || 'Diversifié'}
            </span>
          </div>
        </section>

        {error && (
          <div className="management-alert error">
            <span>⚠️</span>
            {error}
          </div>
        )}
        {feedback && (
          <div className="management-alert success">
            <span>✅</span>
            {feedback}
          </div>
        )}

        <section className="management-section glass-panel">
          <div className="management-section__head">
            <h2 className="management-section__title">Liste des utilisateurs</h2>
            <p className="management-section__subtitle">
              Modifiez les coordonnées sans quitter cette vue et réinitialisez les mots de passe en
              quelques clics. Les actions sont appliquées immédiatement.
            </p>
          </div>
          {users.length === 0 ? (
            <div className="management-empty-state">
              <span className="management-empty-state__icon">📭</span>
              <span className="management-empty-state__title">Aucun utilisateur enregistré</span>
              <span className="management-empty-state__subtitle">
                Créez un premier compte administrateur ou invitez vos équipes pour collaborer sur la
                plateforme.
              </span>
            </div>
          ) : (
            <div className="management-table-scroll">
              <table className="management-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Profil</th>
                    <th>Email</th>
                    <th>Rôle</th>
                    <th>Créé le</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const isEditing = editUserId === user.id;
                    return (
                      <tr key={user.id}>
                        <td>
                          <strong>#{user.id}</strong>
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="text"
                              className="mgmt-input mgmt-input--small"
                              value={editUsername}
                              onChange={(event) => setEditUsername(event.target.value)}
                              placeholder="Nom d'utilisateur"
                            />
                          ) : (
                            user.username
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="email"
                              className="mgmt-input mgmt-input--small"
                              value={editEmail}
                              onChange={(event) => setEditEmail(event.target.value)}
                              placeholder="Adresse email"
                            />
                          ) : (
                            user.email
                          )}
                        </td>
                        <td>
                          <span className="management-tag management-tag--success">
                            {user.role || 'standard'}
                          </span>
                        </td>
                        <td>{formatDateTime(user.created_at)}</td>
                        <td>
                          {isEditing ? (
                            <div className="management-actions">
                              <input
                                type="password"
                                className="mgmt-input mgmt-input--small"
                                placeholder="Nouveau mot de passe"
                                value={editPassword}
                                onChange={(event) => setEditPassword(event.target.value)}
                              />
                              <button
                                type="button"
                                className="mgmt-btn mgmt-btn--primary mgmt-btn--small"
                                onClick={() => handleSaveEdit(user.id)}
                              >
                                Enregistrer
                              </button>
                              <button
                                type="button"
                                className="mgmt-btn mgmt-btn--ghost mgmt-btn--small"
                                onClick={handleCancelEdit}
                              >
                                Annuler
                              </button>
                            </div>
                          ) : (
                            <div className="management-actions">
                              <button
                                type="button"
                                className="mgmt-btn mgmt-btn--outline mgmt-btn--small"
                                onClick={() => handleEdit(user)}
                              >
                                Modifier
                              </button>
                              <button
                                type="button"
                                className="mgmt-btn mgmt-btn--danger mgmt-btn--small"
                                onClick={() => handleDelete(user.id)}
                              >
                                Supprimer
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminUserManagement;